import React, { lazy } from 'react';
import { ExternalLinkIcon, BookOpenIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/layout/PageTransition';
import { DecorativeShape } from '../components/DecorativeShape';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
const SUBSTACK_URL = 'https://helgig.substack.com';
interface Credential {
  code: string;
  label: string;
}
interface TimelineEvent {
  year: string;
  title: string;
  desc: string;
}
interface WritingItem {
  seriesLabel: string;
  title: string;
  description: string;
  tagColor: string;
  url: string;
  articleCount: string;
}
const credentials: Credential[] = [{
  code: 'Fil.Cand.',
  label: 'Work & Org. Psychology'
}, {
  code: 'ACC-level',
  label: 'ICF-accredited coach training'
}, {
  code: 'ICP-LEA',
  label: 'Agile Leadership'
}, {
  code: 'ICP-AHR',
  label: 'Agility in HR'
}, {
  code: 'ICP-BAF',
  label: 'Business Agility'
}, {
  code: 'Clean Language',
  label: 'Systemic Modelling'
}];
const writingEN: WritingItem[] = [{
  seriesLabel: 'On Feedback',
  title: 'The Feedback Paradox',
  description: 'We build entire industries on the assumption that feedback is how people learn and develop. The research is more uncomfortable about this than the mainstream conversation admits. Five articles working through what feedback actually does, why it reveals more about the giver than the receiver, and what to do instead.',
  tagColor: 'bg-coral/10 text-coral',
  url: SUBSTACK_URL,
  articleCount: '5 articles'
}, {
  seriesLabel: 'On Agility',
  title: 'The Lessons of Agility',
  description: 'The Agile movement learned something important about how organisations can navigate complexity, respond to change, and do genuinely good work together. What those lessons actually are - stripped of the brand, the certifications, and the cargo-cult that grew up around them.',
  tagColor: 'bg-teal/10 text-teal',
  url: SUBSTACK_URL,
  articleCount: 'Prologue + 5 articles'
}, {
  seriesLabel: 'On Strategy',
  title: 'The Lessons of Strategy',
  description: 'Most organisations have a strategic plan. Very few have a strategy. The difference matters more than most people realise - and the gap between the two is where most strategic effort goes to die. Five articles on what strategy actually is, why coherence matters, and how to think about it in a changing landscape.',
  tagColor: 'bg-yellow-100 text-yellow-700',
  url: SUBSTACK_URL,
  articleCount: '5 articles'
}, {
  seriesLabel: 'On Teams',
  title: 'Developing Highly Effective Teams',
  description: 'Most people have more bad team experiences than good ones. That is not random - and it is not inevitable. What Wheelan, Tuckman and a body of research on team development actually show about how groups become high-functioning teams, and what most organisations get exactly backwards.',
  tagColor: 'bg-purple-100 text-purple-700',
  url: SUBSTACK_URL,
  articleCount: '4 articles'
}];
const writingIS: WritingItem[] = [{
  seriesLabel: 'Um endurgjöf',
  title: 'Endurgjafar þversögnin',
  description: 'Stór iðnaður hefur skapast kringum þá forsendu að endurgjöf sé lykillinn að þróun. Rannsóknir sýna óþægilegri mynd en við viljum gjarna viðurkenna. Fimm rannsóknarstuddar greinar sem fara m.a. í gegnum hvað endurgjöf gerir í raun, hvers vegna hún segir meira um gefandann en þiggjandann, og hvað við getum gert í staðinn sem virkar betur.',
  tagColor: 'bg-coral/10 text-coral',
  url: SUBSTACK_URL,
  articleCount: '5 greinar'
}, {
  seriesLabel: 'Um hröðun',
  title: 'Lærdómar Agile',
  description: 'Agile-hreyfingin lærði og færði okkur eitthvað mikilvægt um hvernig fyrirtæki geta siglt betur í gegnum flókinn veruleika og skapað öflug teymi sem ná betri og markvissari árangri í þróun. 5 greinar um lykil-lærdóma - óháð vörumerkinu "Agile", vottunum og "yfirborðs-agile" vinnubrögðum sem urðu til.',
  tagColor: 'bg-teal/10 text-teal',
  url: SUBSTACK_URL,
  articleCount: 'Formáli + 5 greinar'
}, {
  seriesLabel: 'Um stefnu',
  title: 'Lærdómur stefnumótunar',
  description: 'Flest fyrirtæki hafa stefnuáætlun. Mjög fá hafa stefnu. Munurinn skiptir meira máli en flestir gera sér grein fyrir. Fimm greinar um hvað stefna er í raun, hvers vegna samræmi skiptir máli, og hvernig við hugsum um það í umhverfi sem breytist.',
  tagColor: 'bg-yellow-100 text-yellow-700',
  url: SUBSTACK_URL,
  articleCount: '5 greinar'
}, {
  seriesLabel: 'Um teymi',
  title: 'Að þróa árangursrík teymi',
  description: 'Flestir hafa meiri slæma reynslu af teymisvinnu en góða. Það er ekki tilviljun - og það er ekki óumflýjanlegt. Hvað Wheelan, Tuckman og rannsóknar á teymisþróun sýna í raun um hvernig hópar verða áhrifarík teymi, og hvað flest fyrirtæki gera nákvæmlega öfugt.',
  tagColor: 'bg-purple-100 text-purple-700',
  url: SUBSTACK_URL,
  articleCount: '4 greinar'
}];
export function About() {
  const {
    t,
    language
  } = useLanguage();
  // Credential wording overridden here (not in translations.ts): coach training completed, certification not held.
  const storyP2 = language === 'en' ? "My background is in Work and Organisational Psychology from the University of Gothenburg, with specialist training in conflict resolution and mediation, and ICF-accredited coach training at ACC level. I'm also co-author of two chapters in the book Agile People Principles, published in 2021." : 'Bakgrunnur minn er í vinnu- og fyrirtækjasálfræði frá Háskólanum í Gautaborg, með aukinni sérhæfingu í lausn ágreininga og sáttamiðlun, auk markþjálfanáms á ACC-stigi (ICF-viðurkennt nám). Ég er einnig meðhöfundur tveggja kafla í bókinni Agile People Principles sem kom út árið 2021.';
  const timelineEvents = (t('about.timeline') as TimelineEvent[]).map(e => e.year === '2013–2017' ? {
    ...e,
    desc: language === 'en' ? 'Fil.Cand. from the University of Gothenburg. Specialist training in conflict resolution and mediation. ICF-accredited coach training (ACC level) at Gothia Academy.' : 'Fil.Cand. frá Gautaborgarháskóla. Sérhæfð þjálfun í úrlausn ágreininga og lóðsun. Markþjálfanám á ACC-stigi (ICF-viðurkennt) hjá Gothia Academy.'
  } : e);
  const publication = t('about.publication') as {
    title: string;
    subtitle: string;
    chapters: string;
  };
  const writing = language === 'is' ? writingIS : writingEN;
  return <PageTransition data-id="element-197" data-mp="gmjrn9qtip02">
      <div className="pt-12 pb-24 overflow-hidden" data-id="element-198" data-mp="gkyx3k4i7627">
        {/* Header */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-199" data-mp="lk243a41nmct">
          <Animate className="text-center max-w-3xl mx-auto relative" data-id="element-200" data-mp="262mcup6o9hjo">
            <DecorativeShape type="circle" color="bg-yellow-200" className="top-0 left-1/2 w-32 h-32 -translate-x-1/2 -translate-y-1/2 -z-10 blur-xl opacity-50" data-id="element-201" data-mp="264yqwr9b77rg" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-202" data-mp="1rdzmztjf57e7">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-203" data-mp="1nc4pl04lze5p">
              {t('about.intro')}
            </p>
          </Animate>
        </section>

        {/* Story & Photo */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-204" data-mp="1de8do63ukf6j">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-205" data-mp="1degqxz3vtmi9">
            <div className="grid grid-cols-2 gap-4" data-id="element-206" data-mp="18gf5x8mwbd45">
              <Animate className="space-y-4 mt-8" data-id="element-207" data-mp="37rzkyyypemyt">
                <img src="https://cdn.magicpatterns.com/uploads/6bt3PcPytwUVW4EyeZ9z5f/Helgi-6_A_height_x30_copy.jpg" alt="Helgi Gudmundsson" className="rounded-3xl shadow-lg w-full h-72 object-cover object-top" loading="lazy" decoding="async" data-id="element-208" data-mp="37rpula9qydsl" />
                <div className="bg-coral p-6 rounded-3xl text-white flex items-center justify-center h-40" data-id="element-209" data-mp="22vqtvphyu54q">
                  <p className="font-heading font-bold text-xl text-center" data-id="element-210" data-mp="26wkhsiverjxm">
                    {t('about.pill1')}
                  </p>
                </div>
              </Animate>
              <Animate delay={0.2} className="space-y-4" data-id="element-211" data-mp="1xw4epaikueta">
                <div className="bg-teal p-6 rounded-3xl text-white flex items-center justify-center h-40" data-id="element-212" data-mp="1xxp82wt5nrpk">
                  <p className="font-heading font-bold text-xl text-center" data-id="element-213" data-mp="1jypklshdpiuz">
                    {t('about.pill2')}
                  </p>
                </div>
                <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Workshop facilitation" className="rounded-3xl shadow-lg w-full h-72 object-cover" loading="lazy" decoding="async" data-id="element-214" data-mp="3ebp6g3ccr9ap" />
              </Animate>
            </div>

            <Animate delay={0.1} className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-215" data-mp="1gfqps0aqdiwe">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-216" data-mp="1blvxw9kz1xm8">
                {t('about.storyTitle')}
              </h2>
              <p data-id="element-217" data-mp="1lds98mc2jk8e">{t('about.storyP1')}</p>
              <p data-id="element-218" data-mp="1lcqotr95xle0">{storyP2}</p>
              <p data-id="element-219" data-mp="1ldieymjzqqww">{t('about.storyP3')}</p>
              <div className="border-l-4 border-teal pl-5 py-1 mt-6" data-id="element-220" data-mp="1ze31ntt116vo">
                <p className="text-sm font-bold text-teal uppercase tracking-wider mb-1" data-id="element-221" data-mp="1zb74w8sx0o8t">
                  {t('about.publicationLabel')}
                </p>
                <p className="font-bold text-charcoal text-base" data-id="element-222" data-mp="50bio3ot5ank">
                  {publication.title}
                </p>
                <p className="text-sm text-gray-500" data-id="element-223" data-mp="29zzjvandcm">{publication.subtitle}</p>
                <p className="text-sm text-gray-500 italic mt-1" data-id="element-224" data-mp="3ceskabgftnjt">
                  {publication.chapters}
                </p>
              </div>
            </Animate>
          </div>
        </section>

        {/* Credentials */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-225" data-mp="33ffbll051305">
          <Animate data-id="element-226" data-mp="2yfsvn3sxd8nn">
            <h2 className="text-lg font-bold text-gray-400 uppercase tracking-wider text-center mb-8" data-id="element-227" data-mp="2ygb36wl1f9av">
              {t('about.credentialsTitle')}
            </h2>
            <div className="flex flex-wrap justify-center gap-3" data-id="element-228" data-mp="3r46v7nznwd1b">
              {credentials.map(c => <div key={c.code} className="flex flex-col items-center bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm" data-id="element-229" data-mp="3mam66lg1qte9">
                  <span className="font-bold text-charcoal text-sm" data-id="element-230" data-mp="invmqoppb9i5">
                    {c.code}
                  </span>
                  <span className="text-gray-400 text-xs mt-0.5" data-id="element-231" data-mp="dv2h7ymerx50">
                    {c.label}
                  </span>
                </div>)}
            </div>
          </Animate>
        </section>

        {/* Timeline */}
        <section className="bg-cream py-24 relative" data-id="element-232" data-mp="v5xvz7kkn0ow">
          <DecorativeShape type="blob" color="bg-teal/10" className="bottom-0 right-0 w-96 h-96 translate-y-1/3 translate-x-1/3" data-id="element-233" data-mp="v4eghj6qoto6" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-234" data-mp="145cki1h6h16r">
            <h2 className="text-3xl font-bold text-center mb-16" data-id="element-235" data-mp="1957b03x1jora">
              {t('about.timelineTitle')}
            </h2>
            <div className="relative" data-id="element-236" data-mp="1dx8lplek97f7">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200 rounded-full hidden md:block" data-id="element-237" data-mp="1dya8uo8upkda" />
              <div className="space-y-12" data-id="element-238" data-mp="1mx5hs83a58ka">
                {timelineEvents.map((event, index) => <Animate key={index} direction={index % 2 === 0 ? 'right' : 'left'} delay={index * 0.1} className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-239" data-mp="1rvp100qho68y" data-mpi={index}>
                    <div className="flex-1 w-full md:w-auto" data-id="element-240" data-mp="1jsas33jnsv3">
                      <Card className="p-6 relative" data-id="element-241" data-mp="1ld8u2p2htxp">
                        <span className="absolute -top-4 -right-4 bg-coral text-white font-bold px-3 py-1 rounded-full text-sm shadow-md" data-id="element-242" data-mp="3sxejoqavhvva">
                          {event.year}
                        </span>
                        <h3 className="text-xl font-bold mb-2 text-teal" data-id="element-243" data-mp="6i3kmdtua3db">
                          {event.title}
                        </h3>
                        <p className="text-gray-600" data-id="element-244" data-mp="fja32sg1to97">{event.desc}</p>
                      </Card>
                    </div>
                    <div className="relative z-10 flex-shrink-0" data-id="element-245" data-mp="2m8y5qtv1eg74">
                      <div className="w-4 h-4 bg-coral rounded-full ring-4 ring-white shadow-md hidden md:block" data-id="element-246" data-mp="2mbklvhvzu40y" />
                    </div>
                    <div className="flex-1 hidden md:block" data-id="element-247" data-mp="wscg90iggxry" />
                  </Animate>)}
              </div>
            </div>
          </div>
        </section>

        {/* Writing */}
        <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-248" data-mp="nz93yvjjqqyw">
          <Animate className="mb-12" data-id="element-249" data-mp="s1nnziyybwrr">
            <div className="flex items-center gap-3 mb-3" data-id="element-250" data-mp="s0kv55i2jk97">
              <BookOpenIcon size={20} className="text-teal" data-id="element-251" data-mp="e2e9qoub3cqf" />
              <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-252" data-mp="e47fm1x3hsxd">
                {t('about.writingLabel')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold" data-id="element-253" data-mp="52hlgobtwfvw">
              {t('about.writingTitle')}
            </h2>
            <p className="text-gray-500 mt-3 text-lg max-w-2xl" data-id="element-254" data-mp="93jnte11l823">
              {t('about.writingSubtitle')}
            </p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-255" data-mp="23gsrgp6wpzcf">
            {writing.map((item, i) => <Animate key={i} delay={i * 0.08} data-id="element-256" data-mp="1eqy6ygo1yg9a" data-mpi={i}>
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="block h-full" data-id="element-257" data-mp="1epn1mceyl0g2">
                  <Card hoverEffect className="h-full p-7 group border-2 border-transparent hover:border-teal/20 transition-colors flex flex-col" data-id="element-258" data-mp="14qe2l558kvmu">
                    <div className="flex items-start justify-between gap-4 mb-4" data-id="element-259" data-mp="vtn9n1lzdwwh">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${item.tagColor}`} data-id="element-260" data-mp="qz9birfxpbo4">
                        {item.seriesLabel}
                      </span>
                      <ExternalLinkIcon size={16} className="text-gray-300 group-hover:text-teal transition-colors flex-shrink-0 mt-0.5" data-id="element-261" data-mp="h0js2u8e3j5r" />
                    </div>
                    <h3 className="font-bold text-lg text-charcoal mb-3 group-hover:text-teal transition-colors leading-snug" data-id="element-262" data-mp="2jpusaszu2q5y">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1" data-id="element-263" data-mp="2spzxcwhk1cgy">
                      {item.description}
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-4 pt-4 border-t border-gray-100" data-id="element-264" data-mp="2xnrt61lbliqf">
                      {item.articleCount}
                    </p>
                  </Card>
                </a>
              </Animate>)}
          </div>

          <Animate delay={0.3} className="text-center" data-id="element-265" data-mp="29z06fpqi1sl1">
            <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-id="element-266" data-mp="29xfapwsg83ll">
              <Button variant="outline" rightIcon={<ExternalLinkIcon size={16} data-id="element-268" data-mp="1hbmgibl7xxaf" />} data-id="element-267" data-mp="2ert9m96bksv0">
                {language === 'is' ? 'Sjá allar greinar á Substack' : 'View all writing on Substack'}
              </Button>
            </a>
          </Animate>
        </section>

        {/* Outside work */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-center" data-id="element-269" data-mp="1a8fvrg6l74yw">
          <Animate data-id="element-270" data-mp="1nzkpy2r6r51u">
            <p className="text-gray-400 text-lg leading-relaxed italic" data-id="element-271" data-mp="1nxzvse8s9o4i">
              {t('about.outsideWork')}
            </p>
          </Animate>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-272" data-mp="v9lvtsy4whqb">
          <Animate data-id="element-273" data-mp="r8sa9685zf0l">
            <Card className="p-10 bg-gradient-to-br from-teal/5 to-coral/5 border-0 text-center" data-id="element-274" data-mp="r9ag8ukmp7lp">
              <h2 className="text-2xl font-bold mb-3" data-id="element-275" data-mp="15932acl7mznc">
                {language === 'is' ? 'Við skulum tala saman' : "Let's talk"}
              </h2>
              <p className="text-gray-500 mb-6 max-w-lg mx-auto" data-id="element-276" data-mp="10bjmv9cs0gtt">
                {language === 'is' ? 'Segðu mér frá stöðunni þinni og við finnum út hvað hentar best. Enginn þrýstingur - bara heiðarleg samtal.' : "Tell me about your situation and we'll find what fits. No pitch - just an honest conversation."}
              </p>
              <Link to="/contact" data-id="element-277" data-mp="2h0lkipw0s0d">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-279" data-mp="3kmvl3axpsvph" />} data-id="element-278" data-mp="2dutqvpvjc0y">
                  {language === 'is' ? 'Hafa samband' : 'Get in touch'}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}